import { useNavigate } from 'react-router-dom';
import IconSvg from 'src/assets/icons/IconSvg';
import Button from 'src/components/button/Button';
import JoinedRaffleCard from '../components/JoinedRaffleCard';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { useViewport } from 'src/context/ViewportContext';
import { useCountdown } from 'src/hooks/useCountdown';
import type { Raffle } from 'src/types/raffle';

const satsToBtc = (sats: number) => (sats / 1e8).toFixed(4);

interface Props {
  activeView: 'list' | 'grid';
  raffles: Raffle[];
  loading: boolean;
  userId: string;
  favoriteRaffleIds: Set<string>;
  onToggleFavorite: (raffleId: string) => void;
  onClaimPrize: (raffleId: string) => void;
}

const JoinedRafflesTab = ({
  activeView,
  raffles,
  loading,
  userId,
  favoriteRaffleIds,
  onToggleFavorite,
  onClaimPrize,
}: Props) => {
  if (loading) {
    return <div className="py-10 text-center text-grayscale-400">Loading raffles...</div>;
  }

  if (raffles.length === 0) {
    return <div className="py-10 text-center text-grayscale-400">No raffles found</div>;
  }

  if (activeView === 'list') {
    return (
      <JoinedRaffleTable
        raffles={raffles}
        userId={userId}
        favoriteRaffleIds={favoriteRaffleIds}
        onToggleFavorite={onToggleFavorite}
        onClaimPrize={onClaimPrize}
      />
    );
  }

  return (
    <div className="grid gap-[18px] grid-cols-[repeat(auto-fill,minmax(320px,1fr))]">
      {raffles.map((raffle) => (
        <JoinedRaffleCard
          key={raffle.id}
          raffle={raffle}
          userId={userId}
          isFavorited={favoriteRaffleIds.has(raffle.id)}
          onToggleFavorite={onToggleFavorite}
          onClaimPrize={onClaimPrize}
        />
      ))}
    </div>
  );
};

export default JoinedRafflesTab;

interface TableProps {
  raffles: Raffle[];
  userId: string;
  favoriteRaffleIds: Set<string>;
  onToggleFavorite: (raffleId: string) => void;
  onClaimPrize: (raffleId: string) => void;
}

const JoinedRaffleTable = ({ raffles, userId, favoriteRaffleIds, onToggleFavorite, onClaimPrize }: TableProps) => {
  const { isMobile } = useViewport();

  return (
    <>
      {!isMobile ? (
        <div className="py-3 rounded-lg bg-grayscale-900">
          <table className="w-full">
            <thead className="text-sm text-left text-grayscale-300">
              <tr>
                <th className="w-[348px] py-[13px] pl-[58px]">Raffle Name</th>

                <th className="w-[188px] py-[13px]">
                  <div className="flex gap-1 items-center">
                    <span>Ticket Price</span>
                    <IconSvg icon="trianglesS" />
                  </div>
                </th>

                <th className="w-[188px] py-[13px]">
                  <div className="flex gap-1 items-center">
                    <span>Sold / Total Tickets</span>
                    <IconSvg icon="trianglesS" />
                  </div>
                </th>

                <th className="w-[188px] py-[13px]">Total Tickets Value</th>

                <th className="w-[188px] py-[13px]">
                  <div className="flex gap-1 items-center">
                    <span>Time Remaining</span>
                    <IconSvg icon="trianglesS" />
                  </div>
                </th>

                <th className="w-[124px] py-5"></th>
              </tr>
            </thead>

            <tbody>
              <tr style={{ height: '10px' }}></tr>

              {raffles.map((raffle) => (
                <JoinedRaffleTableRow
                  key={raffle.id}
                  raffle={raffle}
                  userId={userId}
                  isFavorited={favoriteRaffleIds.has(raffle.id)}
                  onToggleFavorite={onToggleFavorite}
                  onClaimPrize={onClaimPrize}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid gap-2.5">
          {raffles.map((raffle) => (
            <JoinedRaffleMobileRow
              key={raffle.id}
              raffle={raffle}
              isFavorited={favoriteRaffleIds.has(raffle.id)}
              onToggleFavorite={onToggleFavorite}
            />
          ))}
        </div>
      )}
    </>
  );
};

const JoinedRaffleTableRow = ({
  raffle,
  userId,
  isFavorited,
  onToggleFavorite,
  onClaimPrize,
}: {
  raffle: Raffle;
  userId: string;
  isFavorited: boolean;
  onToggleFavorite: (id: string) => void;
  onClaimPrize: (id: string) => void;
}) => {
  const navigate = useNavigate();
  const isEnded = raffle.status === 'ENDED' || raffle.status === 'COMPLETED';
  const userIsWinner = raffle.winners?.some((w) => w.winnerId === userId);
  const countdown = useCountdown(raffle.endDate);
  const endDate = new Date(raffle.endDate);
  const formattedEndDate = `${endDate.getDate()} ${endDate.toLocaleString('en', { month: 'long' })} ${endDate.getFullYear()}`;
  const imageUrl = raffle.ordinalImageUrl || raffle.runesImageUrl;

  return (
    <tr
      className="text-lg rounded-lg transition-all duration-200 cursor-pointer text-grayscale-300 hover:bg-grayscale-700"
      onClick={() => navigate(`/raffle/${raffle.id}`)}
    >
      <td className="py-3.5 pl-5 rounded-l-lg">
        <div className="flex gap-3 items-center">
          <button onClick={e => { e.stopPropagation(); onToggleFavorite(raffle.id); }}>
            <IconSvg
              icon="starM"
              className={isFavorited ? 'text-brand-tangerine' : 'text-grayscale-400'}
            />
          </button>

          <div className="flex gap-3 items-center">
            {imageUrl ? (
              <img src={imageUrl} alt="" className="flex-shrink-0 w-12 h-12 rounded-full object-cover" />
            ) : (
              <div className="flex-shrink-0 w-12 h-12 bg-grayscale-600 rounded-full flex items-center justify-center text-xs text-grayscale-300">
                {raffle.prizeType === 'RUNES' ? 'R' : '?'}
              </div>
            )}

            <div className="space-y-1">
              <div className="flex gap-1 items-center">
                <div className="text-lg font-semibold text-brand-magnolia">
                  {raffle.name ?? 'Untitled'}
                </div>
                <IconSvg icon="verifiedS" className="text-brand-tangerine" />
              </div>
              <span className="text-xss text-grayscale-300">
                Created by: {raffle.creator?.id === userId ? 'You' : 'Creator'}
              </span>
            </div>
          </div>
        </div>
      </td>

      <td>
        <div>
          <div className="flex gap-1 items-center text-grayscale-100">
            <IconSvg icon="bitcoinS" />
            <div className="text-lg font-semibold text-grayscale-100">
              {satsToBtc(raffle.ticketPriceSats)}
            </div>
          </div>
        </div>
      </td>

      <td>{raffle.ticketsSold} / {raffle.supply.toLocaleString()}</td>

      <td>
        <div>
          <div className="flex gap-1 items-center text-grayscale-100">
            <IconSvg icon="bitcoinS" />
            <div className="text-lg font-semibold text-grayscale-100">
              {satsToBtc(raffle.totalTicketValue)}
            </div>
          </div>
        </div>
      </td>

      <td>
        {isEnded ? (
          <div>
            <div className="text-lg text-system-error">Raffle Ended</div>
            <div className="text-xs text-grayscale-400">{formattedEndDate}</div>
          </div>
        ) : (
          <div className="text-lg font-medium text-grayscale-100">{countdown}</div>
        )}
      </td>

      <td className="pr-5 rounded-r-lg">
        {isEnded && userIsWinner && (
          <div className="flex justify-end items-center" onClick={e => e.stopPropagation()}>
            <Button variant="primary" size="sm" onClick={() => onClaimPrize(raffle.id)}>
              Claim Prize
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
};

const JoinedRaffleMobileRow = ({
  raffle,
  isFavorited,
  onToggleFavorite,
}: {
  raffle: Raffle;
  isFavorited: boolean;
  onToggleFavorite: (id: string) => void;
}) => {
  const navigate = useNavigate();
  const isLive = raffle.status === 'LIVE';
  const countdown = useCountdown(raffle.endDate);
  const imageUrl = raffle.ordinalImageUrl || raffle.runesImageUrl;

  return (
    <Disclosure
      as="div"
      className="overflow-hidden relative p-4 rounded-lg bg-grayscale-800 group cursor-pointer"
      onClick={() => navigate(`/raffle/${raffle.id}`)}
    >
      <div className="absolute top-0 left-0 w-[38px] h-[41px] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="w-0 h-0 border-t-[41px] border-t-grayscale-700 border-r-[38px] border-r-transparent">
          <button onClick={() => onToggleFavorite(raffle.id)}>
            <IconSvg
              icon="starM"
              className={`absolute top-[3px] left-[3px] w-[18px] h-[18px] ${isFavorited ? 'text-brand-tangerine' : 'text-grayscale-400'}`}
            />
          </button>
        </div>
      </div>

      <DisclosureButton className="flex relative justify-between items-center w-full rounded-lg group" onClick={e => e.stopPropagation()}>
        <div className="flex gap-3 items-center">
          {imageUrl ? (
            <img src={imageUrl} alt="" className="flex-shrink-0 w-12 h-12 rounded-full object-cover" />
          ) : (
            <div className="flex-shrink-0 w-12 h-12 bg-grayscale-600 rounded-full flex items-center justify-center text-xs text-grayscale-300">
              {raffle.prizeType === 'RUNES' ? 'R' : '?'}
            </div>
          )}

          <div className="space-y-1">
            <div className="flex gap-1 items-center">
              <div className="text-lg font-semibold text-brand-magnolia">
                {raffle.name ?? 'Untitled'}
              </div>
              <IconSvg icon="verifiedS" className="text-brand-tangerine" />
            </div>

            {isLive && (
              <div className="flex gap-1 items-center text-system-success">
                <IconSvg icon="liveS" />
                <span className="leading-none text-xss">{countdown}</span>
              </div>
            )}
          </div>
        </div>

        <IconSvg
          icon="arrowDownM"
          className="text-grayscale-300 group-data-[open]:rotate-180 transition-all duration-200"
        />
      </DisclosureButton>

      <DisclosurePanel className="!mt-3 transition-all duration-200">
        <div className="flex justify-between items-start py-3 border-t border-grayscale-700">
          <div>
            <div className="text-xs text-grayscale-300">Tickets Price</div>
            <div className="flex gap-1 items-center text-grayscale-100">
              <IconSvg icon="bitcoinS" />
              <div className="font-semibold text-grayscale-100">{satsToBtc(raffle.ticketPriceSats)}</div>
            </div>
          </div>

          <div className="text-right">
            <div className="text-xs text-grayscale-300">Sold / Total Tickets</div>
            <div className="font-semibold text-grayscale-100">
              {raffle.ticketsSold} / {raffle.supply.toLocaleString()}
            </div>
          </div>
        </div>

        <div className="flex justify-between items-start py-3 border-t border-grayscale-700">
          <div>
            <div className="text-xs text-grayscale-300">Total Tickets Value</div>
            <div className="flex gap-1 items-center text-grayscale-100">
              <IconSvg icon="bitcoinS" />
              <div className="font-semibold text-grayscale-100">{satsToBtc(raffle.totalTicketValue)}</div>
            </div>
          </div>

          <div className="text-right">
            <div className="text-xs text-grayscale-300">Created by:</div>
            <div className="font-semibold text-grayscale-100">Creator</div>
          </div>
        </div>
      </DisclosurePanel>
    </Disclosure>
  );
};
